/**
 * Custom-column cell for `date_range` columns (monday.com-style "Timeline").
 *
 * The persisted value is `{ start?: string; end?: string }` (ISO `YYYY-MM-DD`).
 * When both ends are set the cell renders a rounded bar labelled with the range
 * and filled to reflect how far TODAY sits between start and end (0% before it
 * starts, 100% once it's past). Clicking opens a range calendar with a Clear
 * action. The Timeline view reuses the same value to plot a span bar.
 */
import { format } from 'date-fns';
import { CalendarRange, X } from 'lucide-react';
import { Button, Calendar, Popover, PopoverContent, PopoverTrigger } from '@monksflow/monks-ui';
import { useState } from 'react';

import { parseLocalDate } from '../../lib/parseLocalDate';
import { EM_DASH } from './constants';
import { useTranslation } from 'react-i18next';

/** Persisted shape of a `date_range` cell value. */
export interface DateRangeValue {
  start?: string;
  end?: string;
}

/** Bar accent (track is a translucent tint of it; fill is a stronger tint). */
const RANGE_COLOR = '#0ea5e9';

interface DateRangeCustomCellProps {
  value: DateRangeValue | undefined;
  onChange: (next: DateRangeValue | null) => void;
  /** Shows the range with no calendar behind it, for someone who cannot change it. */
  readOnly?: boolean;
}

function toISODate(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

/** Elapsed proportion of [start, end] as of today, clamped to 0..100. */
function todayProgress(start: Date, end: Date): number {
  const s = start.getTime();
  const e = end.getTime();
  const now = Date.now();
  if (now <= s) return 0;
  if (now >= e || e <= s) return 100;
  return ((now - s) / (e - s)) * 100;
}

export function DateRangeCustomCell({ value, onChange, readOnly }: DateRangeCustomCellProps) {
  const { t } = useTranslation('trackers');

  const [open, setOpen] = useState(false);
  const start = parseLocalDate(value?.start);
  const end = parseLocalDate(value?.end);
  const hasBoth = start && end;

  const display = hasBoth ? (
    <div
      className="relative h-6 w-full overflow-hidden rounded-md"
      style={{ backgroundColor: `${RANGE_COLOR}26` }}
    >
      <div
        className="absolute inset-y-0 left-0"
        style={{
          width: `${todayProgress(start, end)}%`,
          backgroundColor: `${RANGE_COLOR}66`,
        }}
      />
      <div className="absolute inset-0 flex items-center justify-center gap-1 px-2 text-[11px] font-medium text-foreground">
        <span className="truncate">
          {/* eslint-disable-next-line i18next/no-literal-string -- date-fns format pattern, machine input to format() rather than translator-facing copy */}
          {format(start, 'MMM d')}
        </span>
        <span className="opacity-50">–</span>
        <span className="truncate">
          {/* eslint-disable-next-line i18next/no-literal-string -- date-fns format pattern, machine input to format() rather than translator-facing copy */}
          {format(end, 'MMM d')}
        </span>
      </div>
    </div>
  ) : start ? (
    <span className="flex items-center gap-1.5 text-sm">
      <CalendarRange className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
      {/* eslint-disable-next-line i18next/no-literal-string -- date-fns format pattern is machine input to format(), and the en dash and ellipsis are punctuation only */}
      {format(start, 'MMM d')} – …
    </span>
  ) : readOnly ? (
    <span className="text-muted-foreground">{EM_DASH}</span>
  ) : (
    <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
      <CalendarRange className="size-3.5 shrink-0" aria-hidden />
      {t('trackersBoard.dateRangeCustomCell.setDates')}
    </span>
  );

  if (readOnly) {
    return <div className="flex w-full items-center justify-center px-1 py-0.5">{display}</div>;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="flex w-full items-center justify-center rounded px-1 py-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {display}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0" onClick={(e) => e.stopPropagation()}>
        <Calendar
          mode="range"
          selected={start ? { from: start, to: end } : undefined}
          onSelect={(range) => {
            if (!range?.from) {
              onChange(null);
              return;
            }
            onChange({
              start: toISODate(range.from),
              end: range.to ? toISODate(range.to) : undefined,
            });
          }}
        />
        {start || end ? (
          <div className="border-t border-border p-1">
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start text-muted-foreground"
              onClick={() => {
                onChange(null);
                setOpen(false);
              }}
            >
              <X className="size-3.5" aria-hidden />
              {t('trackersBoard.dateRangeCustomCell.clear')}
            </Button>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
